import { Component, effect, inject, OnDestroy } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormInputComponent } from '@shared/forms';
import { QuizQuestionFacade } from '@app/store/quiz-question';
import { QuizAnswerComponent } from '../quiz-answer/quiz-answer.component';

@Component({
    selector: 'app-quiz-question-add-modal',
    templateUrl: './add.component.html',
    styleUrls: ['./add.component.css'],
    imports: [ReactiveFormsModule, TranslateModule, ButtonModule, FormInputComponent, QuizAnswerComponent],
})
export class AddComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly quizQuestionFacade = inject(QuizQuestionFacade);
    private readonly fb = inject(NonNullableFormBuilder);

    readonly saving = this.quizQuestionFacade.saving;

    readonly form = this.fb.group({
        questionText: this.fb.control('', [FormValidators.Required, FormValidators.Text]),
        answers: this.fb.array([this.createAnswerControl()]),
        correctAnswer: this.fb.control(0),
    });

    get answers() {
        return this.form.controls.answers;
    }

    get correctAnswer() {
        return this.form.controls.correctAnswer;
    }

    constructor() {
        effect(() => {
            if (this.quizQuestionFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.quizQuestionFacade.saving() === false && this.quizQuestionFacade.saved() === false) {
                this.form.enable();
            }
        });
    }

    ngOnDestroy(): void {
        this.quizQuestionFacade.clear();
    }

    addAnswer(): void {
        this.answers.push(this.createAnswerControl());
    }

    removeAnswer(index: number): void {
        this.answers.removeAt(index);

        if (this.correctAnswer.value >= this.answers.length) {
            this.correctAnswer.setValue(this.answers.length - 1);
        }
    }

    onSubmit(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) return;

        this.form.disable();
        const { questionText, answers, correctAnswer } = this.form.getRawValue();

        this.quizQuestionFacade.createQuizQuestion({
            questionText,
            answers: answers.map((answer, index) => ({
                value: answer,
                correct: index === correctAnswer,
            })),
        });
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }

    private createAnswerControl() {
        return this.fb.control('', [FormValidators.Required, FormValidators.Text]);
    }
}
